import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, BadgeDollarSign, BedDouble, Bird, Briefcase, Building2, CalendarClock, Camera, Car,
  CloudSun, Compass, Droplets, Eye, Ghost, GraduationCap, Grape, HeartPulse, Home as HomeIcon, KeyRound,
  Landmark, MapPin, Medal, Mountain, Palette, PawPrint, Pickaxe, Plug, Scale, Search, ShieldCheck,
  ShoppingBag, Sparkles, Star, Store, Sunset, Train, TrendingUp, Truck, Users, UtensilsCrossed, Wine, X,
} from "lucide-react";
import { api, CITIES, CITY_BY_SLUG, EVENTS, FRANK_PHOTO, CAT_IMAGES } from "@/lib/api";
import Testimonials from "@/components/Testimonials";
import { useSeo } from "@/components/Seo";
import Marquee from "@/components/Marquee";
import AskFrankForm from "@/components/AskFrankForm";
import RelocationGuide from "@/components/RelocationGuide";
import FeaturedListings from "@/components/FeaturedListings";
import QuizBand from "@/components/Quiz";
import { CrossPromoCards } from "@/components/CrossPromo";
import { IMAGES } from "@/lib/api";

const CAT_ICONS = {
  "moving-to-sierra-vista": Truck,
  "neighborhoods-safety": ShieldCheck,
  "cost-of-living-comparisons": Scale,
  "home-prices-market": TrendingUp,
  "renting-apartments": KeyRound,
  "neighborhoods-housing-types": Building2,
  "taxes-hoas-55-plus": Landmark,
  "buying-investing": BadgeDollarSign,
  "zip-codes": MapPin,
  "fort-huachuca-military": Medal,
  "jobs-employment": Briefcase,
  "schools-education": GraduationCap,
  "family-life-parks": Users,
  "healthcare-facilities": HeartPulse,
  "weather-climate": CloudSun,
  "utilities-internet": Plug,
  "transportation-commuting": Car,
  "things-to-do-outdoors": Mountain,
  "restaurants-dining": UtensilsCrossed,
  "shopping-local-life": ShoppingBag,
  "hereford-general": MapPin,
  "hereford-living": HomeIcon,
  "hereford-real-estate": TrendingUp,
  "hereford-rentals": KeyRound,
  "hereford-land": Mountain,
  "hereford-schools": GraduationCap,
  "hereford-weather": CloudSun,
  "hereford-jobs": Briefcase,
  "hereford-restaurants": UtensilsCrossed,
  "hereford-shopping": ShoppingBag,
  "hereford-healthcare": HeartPulse,
  "hereford-vets": PawPrint,
  "hereford-outdoors": Mountain,
  "hereford-birding": Bird,
  "hereford-tourism": Camera,
  "hereford-transportation": Car,
  "hereford-utilities": Plug,
  "huachuca-city-general": Sunset,
  "huachuca-city-living": HomeIcon,
  "huachuca-city-vs": Scale,
  "huachuca-city-zip-codes": MapPin,
  "huachuca-city-real-estate": TrendingUp,
  "huachuca-city-land": Mountain,
  "huachuca-city-rentals": KeyRound,
  "huachuca-city-schools": GraduationCap,
  "huachuca-city-fort": Medal,
  "huachuca-city-jobs": Briefcase,
  "huachuca-city-restaurants": UtensilsCrossed,
  "huachuca-city-shopping": ShoppingBag,
  "huachuca-city-utilities": Plug,
  "huachuca-city-water": Droplets,
  "huachuca-city-healthcare": HeartPulse,
  "huachuca-city-pets": PawPrint,
  "huachuca-city-things-to-do": Compass,
  "bisbee-things-to-do": Compass,
  "bisbee-queen-mine": Pickaxe,
  "bisbee-history": Landmark,
  "bisbee-old-town": Store,
  "bisbee-restaurants": UtensilsCrossed,
  "bisbee-hotels": BedDouble,
  "bisbee-parking": Car,
  "bisbee-weather": CloudSun,
  "bisbee-ghosts": Ghost,
  "bisbee-arts": Palette,
  "bisbee-outdoors": Mountain,
  "bisbee-living": HomeIcon,
  "bisbee-real-estate": TrendingUp,
  "tombstone-things-to-do": Compass,
  "tombstone-ok-corral": ShieldCheck,
  "tombstone-history": Landmark,
  "tombstone-allen-street": Store,
  "tombstone-restaurants": UtensilsCrossed,
  "tombstone-hotels": BedDouble,
  "tombstone-directions": Car,
  "tombstone-weather": CloudSun,
  "tombstone-ghosts": Ghost,
  "tombstone-events": CalendarClock,
  "tombstone-outdoors": Mountain,
  "tombstone-living": HomeIcon,
  "tombstone-real-estate": TrendingUp,
  "benson-about": MapPin,
  "benson-living": HomeIcon,
  "benson-housing": TrendingUp,
  "benson-weather": CloudSun,
  "benson-kartchner": Mountain,
  "benson-things-to-do": Compass,
  "benson-history-western": Camera,
  "benson-restaurants": UtensilsCrossed,
  "benson-hotels": BedDouble,
  "benson-schools": GraduationCap,
  "benson-jobs": Briefcase,
  "benson-healthcare": HeartPulse,
  "benson-transportation": Car,
  "benson-utilities": Plug,
  "benson-safety": ShieldCheck,
  "benson-parks": Users,
  "benson-shopping": Store,
  "benson-zip-85602": Landmark,
  "benson-nearby": MapPin,
  "benson-visitors": Sparkles,
  "benson-local-info": CalendarClock,
  "douglas-about": MapPin,
  "douglas-living": HomeIcon,
  "douglas-housing": TrendingUp,
  "douglas-border": ShieldCheck,
  "douglas-history": Landmark,
  "douglas-things-to-do": Compass,
  "douglas-restaurants": UtensilsCrossed,
  "douglas-hotels": BedDouble,
  "douglas-schools": GraduationCap,
  "douglas-jobs": Briefcase,
  "douglas-healthcare": HeartPulse,
  "douglas-transportation": Car,
  "douglas-weather": CloudSun,
  "douglas-utilities": Plug,
  "douglas-safety": ShieldCheck,
  "douglas-outdoors": Mountain,
  "douglas-shopping": Store,
  "douglas-nearby": MapPin,
  "douglas-visitors": Sparkles,
  "douglas-local-info": CalendarClock,
  "willcox-about": MapPin,
  "willcox-living": HomeIcon,
  "willcox-housing": TrendingUp,
  "willcox-wine": Wine,
  "willcox-rex-allen": Star,
  "willcox-things-to-do": Compass,
  "willcox-chiricahua": Mountain,
  "willcox-restaurants": UtensilsCrossed,
  "willcox-hotels": BedDouble,
  "willcox-schools": GraduationCap,
  "willcox-jobs": Briefcase,
  "willcox-healthcare": HeartPulse,
  "willcox-transportation": Car,
  "willcox-weather": CloudSun,
  "willcox-utilities": Plug,
  "willcox-shopping": Store,
  "willcox-nearby": MapPin,
  "willcox-events": CalendarClock,
  "willcox-outdoors": Bird,
  "sahuarita-about": MapPin,
  "sahuarita-living": HomeIcon,
  "sahuarita-housing": TrendingUp,
  "sahuarita-rancho-sahuarita": Building2,
  "sahuarita-rentals": KeyRound,
  "sahuarita-schools": GraduationCap,
  "sahuarita-jobs": Briefcase,
  "sahuarita-weather": CloudSun,
  "sahuarita-things-to-do": Compass,
  "sahuarita-restaurants": UtensilsCrossed,
  "sahuarita-shopping": ShoppingBag,
  "sahuarita-healthcare": HeartPulse,
  "sahuarita-transportation": Car,
  "sahuarita-safety": ShieldCheck,
  "sahuarita-parks": Users,
  "sahuarita-utilities": Plug,
  "sahuarita-nearby": MapPin,
  "sahuarita-visitors": Camera,
  "sahuarita-local-info": CalendarClock,
  "vail-about": MapPin,
  "vail-living": HomeIcon,
  "vail-housing": TrendingUp,
  "vail-neighborhoods": Building2,
  "vail-rentals": KeyRound,
  "vail-schools": GraduationCap,
  "vail-jobs": Briefcase,
  "vail-weather": CloudSun,
  "vail-things-to-do": Compass,
  "vail-restaurants": UtensilsCrossed,
  "vail-shopping": ShoppingBag,
  "vail-healthcare": HeartPulse,
  "vail-transportation": Car,
  "vail-safety": ShieldCheck,
  "vail-utilities": Plug,
  "vail-history": Train,
  "vail-nearby": Mountain,
  "vail-visitors": Camera,
  "vail-local-info": CalendarClock,
  "sonoita-about": MapPin,
  "sonoita-living": HomeIcon,
  "sonoita-real-estate": TrendingUp,
  "sonoita-wine": Grape,
  "sonoita-things-to-do": Compass,
  "sonoita-restaurants": UtensilsCrossed,
  "sonoita-lodging": BedDouble,
  "sonoita-weather": CloudSun,
  "sonoita-schools": GraduationCap,
  "sonoita-jobs": Briefcase,
  "sonoita-healthcare": HeartPulse,
  "sonoita-transportation": Car,
  "sonoita-utilities": Plug,
  "sonoita-events": CalendarClock,
  "sonoita-outdoors": Bird,
  "sonoita-nearby": MapPin,
  "sonoita-history": Landmark,
  "sonoita-safety": ShieldCheck,
  "sonoita-local-info": CalendarClock,
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ children, i = 0, className = "" }) {
  return (
    <motion.div className={className} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
      {children}
    </motion.div>
  );
}

function HeroLine({ text, delay }) {
  const parts = text.split("*");
  return (
    <span className="mask-line">
      <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
        {parts.map((p, i) =>
          i % 2 === 1 ? <em key={i} className="text-brand-gold font-display italic">{p}</em> : <span key={i}>{p}</span>
        )}
      </motion.span>
    </span>
  );
}

function HeroSub({ text }) {
  const parts = text.split("**");
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? <strong key={i} className="text-white">{p}</strong> : <span key={i}>{p}</span>
      )}
    </>
  );
}

export default function Home({ city = "sierra-vista", askFrankRef }) {
  const cfg = CITY_BY_SLUG[city] || CITIES[0];

  useSeo({ title: cfg.seoTitle, description: cfg.seoDesc, path: cfg.path });

  const [daily, setDaily] = useState(null);
  const [cats, setCats] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [mostRead, setMostRead] = useState([]);
  const [roundup, setRoundup] = useState(null);
  const [comparePairs, setComparePairs] = useState([]);
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [status, setStatus] = useState("all");
  const browseRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    setDaily(null);
    setSearch("");
    setCatFilter("");
    setStatus("all");
    api.get("/daily", { params: { city } }).then((r) => setDaily(r.data)).catch(() => {});
    api.get("/categories", { params: { city } }).then((r) => setCats(r.data.categories)).catch(() => {});
    api.get("/questions", { params: { city } }).then((r) => setQuestions(r.data.questions)).catch(() => {});
    api.get("/featured", { params: { city } }).then((r) => setFeatured(r.data.featured)).catch(() => {});
    api.get("/most-read", { params: { city } }).then((r) => setMostRead(r.data.most_read)).catch(() => {});
    api.get("/roundups", { params: { city } }).then((r) => setRoundup(r.data.roundups[0] || null)).catch(() => {});
    api.get("/compare").then((r) => setComparePairs(r.data.pairs)).catch(() => {});
    api.get("/featured-listings").then((r) => setListings(r.data.listings)).catch(() => {});
  }, [city]);

  const filtered = questions.filter((q) => {
    if (catFilter && q.category !== catFilter) return false;
    if (status === "answered" && !q.answered) return false;
    if (status === "upcoming" && q.answered) return false;
    if (search && !q.question.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const heroSearch = (e) => {
    e.preventDefault();
    browseRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const answeredCount = daily?.stats?.answered ?? 0;
  const totalCount = daily?.stats?.total ?? 365;
  const latest = daily?.latest;

  return (
    <div>
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative overflow-hidden bg-brand-navy-dark grain" data-testid="hero-section">
        <motion.img
          src={cfg.image}
          alt={cfg.imageAlt}
          style={{ y: heroImgY }}
          className="absolute inset-0 h-[115%] w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-navy-dark/70 via-brand-navy-dark/40 to-brand-navy-dark" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-brand-gold/30 bg-white/5 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.25em] text-brand-gold"
            >
              <Sparkles className="h-3.5 w-3.5" /> {cfg.eyebrow}
            </motion.p>
            <h1 className="mt-7 font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
              {cfg.heroLines.map((line, i) => (
                <HeroLine key={`${city}-${i}`} text={line} delay={0.15 + i * 0.15} />
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
            >
              <HeroSub text={cfg.heroSub} />
            </motion.p>
            <motion.form
              onSubmit={heroSearch}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-8 flex max-w-xl items-center gap-2 rounded-full border border-white/15 bg-white/10 p-2 backdrop-blur-md"
            >
              <Search className="ml-3 h-4 w-4 shrink-0 text-white/60" />
              <input
                data-testid="hero-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={cfg.heroSearchPlaceholder}
                className="w-full bg-transparent text-sm text-white placeholder:text-white/45 focus:outline-none"
              />
              <button data-testid="hero-search-button" type="submit" className="shrink-0 rounded-full bg-brand-red px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-red-dark">
                Search
              </button>
            </motion.form>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }}
              className="mt-8 flex flex-wrap gap-2.5 text-[11px] font-mono uppercase tracking-[0.18em] text-white/60"
            >
              {["365 Questions", `${cats.length || "…"} Categories`, cfg.zips, "1 New Answer Daily"].map((chip) => (
                <span key={chip} className="rounded-full border border-white/15 px-3.5 py-1.5">{chip}</span>
              ))}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block lg:col-span-5"
          >
            <div className="editorial-frame relative overflow-hidden rounded-2xl">
              <img src={cfg.image} alt={cfg.imageAlt} fetchPriority="high" className="h-96 w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-dark/90 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-gold">Answer #{latest?.number ?? "…"} of 365</p>
                <p className="mt-1.5 font-display text-lg font-bold leading-snug text-white line-clamp-2">
                  {latest ? latest.question : "Today's answer is being prepared…"}
                </p>
                {latest && (
                  <Link data-testid="hero-latest-answer-link" to={`/answers/${latest.slug}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-brand-gold hover:text-white transition-colors">
                    Read today's answer <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee items={cfg.marquee} />

      {/* ---------- TODAY'S ANSWER ---------- */}
      <section id="todays-answer" data-testid="todays-answer-section" className="bg-brand-sand py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <Reveal className="lg:col-span-7">
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold">Fresh from the AI editor</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-brand-navy-dark">Today's Answer</h2>
              {latest ? (
                <div className="mt-8 rounded-2xl border border-brand-border-sand bg-white p-7 sm:p-9 shadow-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/15 border border-brand-gold/40 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-gold-warm">
                      <Sparkles className="h-3.5 w-3.5" /> AI Answer · {cats.find((c) => c.slug === latest.category)?.title || latest.category}
                    </span>
                    <span className="text-xs text-brand-slate/60 font-mono">Q#{String(latest.number).padStart(3, "0")}</span>
                  </div>
                  <h3 className="mt-5 font-display text-2xl sm:text-3xl font-bold leading-snug text-brand-navy-dark">{latest.question}</h3>
                  <p className="mt-4 text-base sm:text-lg leading-relaxed text-brand-slate">{latest.direct_answer}</p>
                  <Link data-testid="todays-answer-read-more" to={`/answers/${latest.slug}`}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-navy-dark">
                    Read the full answer <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <div className="mt-8 rounded-2xl border border-dashed border-brand-border-sand bg-white/60 p-9 text-brand-slate">
                  The first daily answer for {cfg.name} is being written — check back shortly.
                </div>
              )}
            </Reveal>
            <Reveal i={1} className="lg:col-span-5">
              <div className="rounded-2xl bg-brand-navy-dark p-7 sm:p-8 text-white grain relative overflow-hidden">
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-gold">The {cfg.name} 365 Project</p>
                <p className="mt-4 font-display text-5xl font-black">
                  {answeredCount}<span className="text-white/40 text-3xl"> / {totalCount}</span>
                </p>
                <p className="mt-2 text-sm text-white/60">questions answered in full</p>
                <div className="mt-5 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-brand-gold to-brand-red transition-all duration-700" style={{ width: `${daily?.stats?.percent ?? 0}%` }} />
                </div>
                {daily?.next && (
                  <div className="mt-7 rounded-xl border border-white/10 bg-white/5 p-5">
                    <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/50 flex items-center gap-1.5">
                      <CalendarClock className="h-3.5 w-3.5" /> Tomorrow the AI answers
                    </p>
                    <p className="mt-2 text-sm font-semibold leading-snug text-white/85">{daily.next.question}</p>
                  </div>
                )}
                {roundup && (
                  <Link data-testid="weekly-roundup-link" to={`/roundup/${roundup.slug}`}
                    className="mt-4 flex items-center justify-between rounded-xl border border-brand-gold/30 bg-brand-gold/10 px-5 py-4 transition-colors hover:bg-brand-gold/20">
                    <span className="text-sm font-bold text-brand-gold">This week in {cfg.name} — {roundup.count} new {roundup.count === 1 ? "answer" : "answers"}</span>
                    <ArrowRight className="h-4 w-4 text-brand-gold" />
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- FEATURED ANSWERS ---------- */}
      {featured.length > 0 && (
        <section data-testid="featured-answers-strip" className="bg-brand-sand pb-2 -mt-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-4">
            <Reveal>
              <div className="rounded-2xl border border-brand-gold/40 bg-white p-6 sm:p-7">
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold flex items-center gap-2">
                  <Star className="h-4 w-4 fill-brand-gold text-brand-gold" /> Frank's featured {cfg.name} answers
                </p>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featured.slice(0, 6).map((f) => (
                    <Link
                      key={f.slug}
                      data-testid={`featured-answer-${f.slug}`}
                      to={`/answers/${f.slug}`}
                      className="group rounded-xl border border-brand-border-sand bg-brand-sand p-5 transition-all duration-300 hover:-translate-y-1 hover:border-brand-navy/30"
                    >
                      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand-terracotta">{f.category_title}</p>
                      <p className="mt-2 text-sm font-bold leading-snug text-brand-navy-dark group-hover:text-brand-red transition-colors line-clamp-2">{f.question}</p>
                      <p className="mt-2 text-xs leading-relaxed text-brand-slate line-clamp-3">{f.direct_answer}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------- MOST READ ---------- */}
      {mostRead.length > 0 && (
        <section data-testid="most-read-strip" className="bg-brand-sand pb-2 -mt-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-4">
            <Reveal>
              <div className="rounded-2xl border border-brand-border-sand bg-white p-6 sm:p-7">
                <p className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold">
                  <Eye className="h-4 w-4 text-brand-red" /> Most read {cfg.name} answers
                </p>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mostRead.slice(0, 6).map((m) => (
                    <Link
                      key={m.slug}
                      data-testid={`most-read-${m.slug}`}
                      to={`/answers/${m.slug}`}
                      className="group rounded-xl border border-brand-border-sand bg-brand-sand p-5 transition-all duration-300 hover:-translate-y-1 hover:border-brand-navy/30"
                    >
                      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand-terracotta">{m.category_title}</p>
                      <p className="mt-2 text-sm font-bold leading-snug text-brand-navy-dark transition-colors group-hover:text-brand-red line-clamp-2">{m.question}</p>
                      <p className="mt-2 text-[11px] font-mono text-brand-slate/60">{m.views} {m.views === 1 ? "read" : "reads"}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------- TOWN QUIZ ---------- */}
      <section className="bg-brand-sand py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <QuizBand cityName={cfg.name} />
          </Reveal>
        </div>
      </section>

      {/* ---------- COMPARE TOWNS ---------- */}
      {comparePairs.length > 0 && (
        <section data-testid="compare-strip" className="bg-brand-sand pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <Reveal>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-brand-border-sand bg-white px-6 py-5">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-brand-terracotta font-bold">Compare the towns</span>
                {comparePairs.map((p) => (
                  <Link
                    key={p.slug}
                    data-testid={`compare-link-${p.slug}`}
                    to={`/compare/${p.slug}`}
                    className="rounded-full border border-brand-border-sand px-4 py-1.5 text-xs font-bold text-brand-navy transition-colors hover:border-brand-navy/40 hover:text-brand-red"
                  >
                    {p.a} vs {p.b}
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------- CATEGORIES ---------- */}
      <section id="categories" data-testid="categories-grid" className="bg-brand-sand-dark py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold">{cfg.catEyebrow}</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-brand-navy-dark">{cfg.catHeading}</h2>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-brand-slate">{cfg.catSub}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5">
            {cats.map((cat, i) => {
              const Icon = CAT_ICONS[cat.slug] || Mountain;
              const img = CAT_IMAGES[cat.slug];
              const heroCard = i === 0;
              const span = heroCard ? "md:col-span-8 lg:col-span-8" : "md:col-span-4 lg:col-span-4";
              return (
                <Reveal key={cat.slug} i={i % 6} className={span}>
                  <Link
                    data-testid={`category-card-${cat.slug}`}
                    to={`/category/${cat.slug}`}
                    className={`group relative flex h-full min-h-[190px] flex-col justify-between overflow-hidden rounded-2xl p-6 sm:p-7 transition-transform duration-300 hover:-translate-y-1.5 ${
                      img ? "text-white" : "border border-brand-border-sand bg-white text-brand-navy-dark hover:border-brand-navy/30"
                    }`}
                  >
                    {img && (
                      <>
                        <img src={img} alt={`${cat.title} — ${cfg.name}, Arizona`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-dark/95 via-brand-navy-dark/45 to-brand-navy-dark/20" />
                      </>
                    )}
                    <div className="relative flex items-start justify-between">
                      <span className={`grid h-11 w-11 place-items-center rounded-xl ${img ? "bg-white/15 backdrop-blur-sm text-brand-gold" : "text-brand-navy bg-brand-sand-dark"}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <ArrowRight className={`h-5 w-5 transition-all duration-300 group-hover:translate-x-1 ${img ? "text-brand-gold" : "text-brand-red"}`} />
                    </div>
                    <div className="relative mt-8">
                      <h3 className={`font-display font-bold leading-snug ${heroCard ? "text-2xl sm:text-3xl" : "text-xl"}`}>{cat.title}</h3>
                      <p className={`mt-1.5 text-xs sm:text-sm ${img ? "text-white/70" : "text-brand-slate/80"}`}>{cat.tagline}</p>
                      <p className={`mt-3 text-[11px] font-mono uppercase tracking-[0.18em] ${img ? "text-brand-gold" : "text-brand-terracotta"}`}>
                        {cat.answered} / {cat.total} answered
                      </p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- RELOCATION GUIDE ---------- */}
      <section id="relocation-guide" data-testid="relocation-guide-section" className="bg-brand-sand-dark pb-16 sm:pb-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <RelocationGuide city={city} cityName={cfg.name} />
          </Reveal>
        </div>
      </section>

      {/* ---------- BROWSE 365 ---------- */}
      <section id="browse-questions" ref={browseRef} data-testid="browse-questions-section" className="bg-brand-sand py-16 sm:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold">The full {cfg.name} index</p>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-brand-navy-dark">Browse all 365 questions</h2>
              </div>
              <p className="text-sm text-brand-slate lg:text-right">Showing <strong className="text-brand-navy-dark">{filtered.length}</strong> of {questions.length || 365} questions</p>
            </div>
          </Reveal>
          <Reveal i={1} className="mt-8">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-slate/50" />
                <input
                  data-testid="search-questions-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search all 365 ${cfg.name} questions…`}
                  className="w-full rounded-full border border-brand-border-sand bg-white py-3.5 pl-11 pr-11 text-sm text-brand-navy-dark placeholder:text-brand-slate/50 focus:outline-none focus:ring-2 focus:ring-brand-navy/30"
                />
                {(search || catFilter || status !== "all") && (
                  <button data-testid="search-reset-button" onClick={() => { setSearch(""); setCatFilter(""); setStatus("all"); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-brand-slate/60 hover:bg-brand-sand-dark hover:text-brand-red transition-colors" aria-label="Reset search">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <select
                data-testid="category-filter-select"
                value={catFilter}
                onChange={(e) => setCatFilter(e.target.value)}
                className="rounded-full border border-brand-border-sand bg-white px-5 py-3.5 text-sm font-semibold text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/30"
              >
                <option value="">All {cats.length || ""} categories</option>
                {cats.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.title}</option>
                ))}
              </select>
              <div data-testid="status-filter-toggle" className="flex rounded-full border border-brand-border-sand bg-white p-1 text-xs font-bold">
                {[["all", "All 365"], ["answered", "Answered"], ["upcoming", "Coming Soon"]].map(([val, label]) => (
                  <button key={val} data-testid={`status-filter-${val}`} onClick={() => setStatus(val)}
                    className={`rounded-full px-4 py-2.5 transition-colors ${status === val ? "bg-brand-navy text-white" : "text-brand-slate hover:text-brand-navy"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
          <div className="mt-8 divide-y divide-brand-border-sand overflow-hidden rounded-2xl border border-brand-border-sand bg-white">
            {filtered.slice(0, 120).map((q) => (
              <Link
                key={q.slug}
                data-testid={`question-row-${q.slug}`}
                to={`/answers/${q.slug}`}
                className="group flex items-center gap-4 px-5 sm:px-7 py-4 transition-colors hover:bg-brand-sand"
              >
                <span className="hidden sm:block w-12 shrink-0 font-mono text-xs text-brand-slate/50">#{String(q.number).padStart(3, "0")}</span>
                <span className="flex-1 text-sm sm:text-base font-semibold text-brand-navy-dark group-hover:text-brand-red transition-colors">{q.question}</span>
                {q.answered ? (
                  <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand-gold/15 border border-brand-gold/40 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-gold-warm">
                    <Sparkles className="h-3 w-3" /> Answered
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-brand-sand-dark border border-brand-border-sand px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-slate/60">
                    Coming Soon
                  </span>
                )}
                <ArrowRight className="h-4 w-4 shrink-0 text-brand-slate/30 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand-red" />
              </Link>
            ))}
            {filtered.length === 0 && (
              <p className="px-7 py-14 text-center text-sm text-brand-slate">No questions match — try a different search.</p>
            )}
          </div>
          {filtered.length > 120 && (
            <p className="mt-4 text-center text-xs font-mono text-brand-slate/60">Showing first 120 matches — refine your search to narrow down.</p>
          )}
        </div>
      </section>

      {/* ---------- FEATURED LISTINGS ---------- */}
      {listings.length > 0 && (
        <section data-testid="listings-section" className="bg-brand-sand pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <Reveal>
              <FeaturedListings listings={listings} cityName={cfg.name} />
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------- EVENTS ---------- */}
      {(EVENTS[city] || []).length > 0 && (
        <section data-testid="events-section" className="bg-brand-sand pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <Reveal>
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold">Mark the calendar</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brand-navy-dark">Events around {cfg.name}</h2>
              <p className="mt-3 max-w-2xl text-sm text-brand-slate">Annual highlights — dates shift each year, so verify before you go.</p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(EVENTS[city] || []).map((e, i) => (
                <Reveal key={e.name} i={i % 4}>
                  <div className="h-full rounded-2xl border border-brand-border-sand bg-white p-6 transition-transform duration-300 hover:-translate-y-1">
                    <span className="inline-block rounded-lg bg-brand-navy px-3 py-1.5 text-[11px] font-mono font-bold tracking-[0.15em] text-brand-gold">{e.month}</span>
                    <p className="mt-4 font-display text-lg font-bold leading-snug text-brand-navy-dark">{e.name}</p>
                    <p className="mt-2 text-sm leading-relaxed text-brand-slate">{e.detail}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- CROSS PROMO ---------- */}
      <section className="bg-brand-sand-dark py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold">From the same desk</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brand-navy-dark">Frank Powell's family of sites</h2>
          </Reveal>
          <Reveal i={1} className="mt-10">
            <CrossPromoCards />
          </Reveal>
        </div>
      </section>

      {/* ---------- ABOUT FRANK ---------- */}
      <section id="about-frank" data-testid="about-frank-section" className="bg-brand-navy-dark py-16 sm:py-24 grain relative scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <div className="editorial-frame relative overflow-hidden rounded-2xl mx-auto max-w-xs">
              <img src={FRANK_PHOTO} alt="Frank Powell, Realtor at Service First Realty, Sierra Vista" loading="lazy" decoding="async" className="w-full object-cover object-top aspect-square" />
            </div>
          </Reveal>
          <Reveal i={1} className="lg:col-span-8">
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-gold font-semibold">The man behind the almanac</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">Frank Powell</h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-white/70 max-w-2xl">
              <p>
                Frank served in both the U.S. Navy and Army before building a near-20-year delivery business, and today holds both an Arizona real estate license and a notary license. As a property owner who has owned several homes across Southeast Arizona, he answers these local questions from lived experience — not a brochure.
              </p>
              <p>
                He works with the <a href="https://yourrealtorfrank.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-gold underline decoration-brand-gold/40 underline-offset-4 hover:text-white transition-colors">Sierra Vista Home Selling Team at Service First Realty</a>, helping families buy and sell within a 60-mile radius of Sierra Vista — including {cfg.name} — and teaches the next generation of agents at <a href="https://yourrealtorfrank.net" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-gold underline decoration-brand-gold/40 underline-offset-4 hover:text-white transition-colors">yourrealtorfrank.net</a>.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-2.5 text-[11px] font-mono uppercase tracking-[0.18em] text-white/60">
              {["U.S. Navy & Army Veteran", "Licensed AZ Realtor", "Licensed Notary", "Father of 8"].map((b) => (
                <span key={b} className="rounded-full border border-white/15 px-3.5 py-1.5">{b}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- ASK FRANK ---------- */}
      <Testimonials />

      <section id="ask-frank" ref={askFrankRef} data-testid="ask-frank-section" className="bg-brand-sand py-16 sm:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-brand-terracotta font-semibold">Can't wait for tomorrow's answer?</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-brand-navy-dark leading-tight">Ask Frank directly.</h2>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-brand-slate max-w-md">
              The AI answers one question a day — Frank answers yours personally. Relocation timelines, land, acreage, VA loans, Fort Huachuca commutes: if it's {cfg.name} or anywhere in Southeast Arizona, he's your guy.
            </p>
            <ul className="mt-7 space-y-3 text-sm text-brand-slate">
              {["Personal reply from Frank Powell, Service First Realty", `60-mile ${cfg.name} expertise and beyond`, "Veteran and PCS relocation specialist"].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-gold" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal i={1} className="lg:col-span-7">
            <div className="rounded-2xl border border-brand-border-sand bg-white p-7 sm:p-9 shadow-sm">
              <AskFrankForm sourceCity={city} />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
